import React from "react";
import {
  Box,
  alpha, // Import alpha for color manipulation
} from "@mui/material";
import Seo from "../components/Seo";
import { useInView } from "react-intersection-observer";
import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";

import { ThemeProvider, createTheme } from "@mui/material/styles";

import HeroSection from "../pages/buyer/HeroSection";
import HowItWorksSection from "../pages/buyer/HowItWorksSection";
import MarketStatisticsSection from "../pages/buyer/MarketStatisticsSection";
import StaticsHomeSection from '../pages/buyer/StaticsHomeSection';
import WhyChooseScrapTradeSection from "../pages/buyer/WhyChooseScrapTradeSection";
import TestimonialsSection from "../pages/buyer/TestimonialsSection";
import FAQSection from "../pages/buyer/FAQSection";
import {
  FaChartBar,
  FaCheckCircle,
  FaClipboardList,
  FaCreditCard,
  FaDollarSign,
  FaMoneyBillWave,
  FaTruck,
  FaUser,
} from "react-icons/fa";
import RandomProductHomeSection from "./buyer/RandomProductHomeSection";
import TrustAndCredibility from "../components/buyer/TrustAndCredibility";
import CTAHomeSection from "./buyer/CTAHomeSection";

// Define theme colors for consistency and easier modification
const themeColors = {
  primary: "#F4C542",
  secondary: "#2D3A27",
  backgroundLight: "#ffffff",
  backgroundDark: "#fafafa",
  textPrimary: "#1a1a1a",
  textSecondary: "rgba(26, 26, 26, 0.7)",
  cardBackgroundLight: "rgba(255, 255, 255, 0.9)",
  cardBackgroundDark: "linear-gradient(135deg, #2D3A27 0%, #1a2118 100%)",
};

// Create a custom Material-UI theme
const theme = createTheme({
  typography: {
    fontFamily: "Inter, sans-serif", // Use Inter font
  },
  palette: {
    primary: {
      main: themeColors.primary,
    },
    secondary: {
      main: themeColors.secondary,
    },
    background: {
      default: themeColors.backgroundLight,
    },
    text: {
      primary: themeColors.textPrimary,
      secondary: themeColors.textSecondary,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 16, // Apply rounded corners to buttons
          textTransform: "none", // Prevent uppercase text
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16, // Apply rounded corners to cards
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16, // Apply rounded corners to Paper components
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 12, // Apply rounded corners to Accordion
          "&.Mui-expanded": {
            margin: "16px 0", // Adjust margin when expanded
          },
          "&:before": {
            display: "none", // Remove default border
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 12, // Apply rounded corners to AccordionSummary
          backgroundColor: themeColors.backgroundLight,
          "&.Mui-expanded": {
            minHeight: 48, // Maintain height when expanded
          },
        },
        content: {
          margin: "12px 0", // Adjust content margin
          "&.Mui-expanded": {
            margin: "12px 0",
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "16px 24px 24px", // Adjust padding
          borderTop: `1px solid ${alpha(themeColors.textPrimary, 0.1)}`, // Add a subtle border
        },
      },
    },
  },
});

const Landing = () => {
  // const [tab, setTab] = useState(0);
  const { ref: statsRef, inView: statsInView } = useInView({
    triggerOnce: true,
  });
  const { ref: heroRef, inView: heroInView } = useInView({ triggerOnce: true });
  const { ref: featuresRef, inView: featuresInView } = useInView({
    triggerOnce: true,
  });
  const { ref: howItWorksRef, inView: howItWorksInView } = useInView({
    triggerOnce: true,
  });

  const sectionRefs = useRef([]);
  const heroElements = useRef([]);
  const featureElements = useRef([]);
  const howItWorksElements = useRef([]);

  // Ref for the auto-scrolling "How It Works" section
  const howItWorksScrollRef = useRef(null);

  // Ref for the new auto-scrolling Testimonial section
  const testimonialsScrollRef = useRef(null);
  const scrollIntervalRef = useRef(null); // To store the interval ID for testimonials

  // State for "How It Works" section tabs
  const [activeRole, setActiveRole] = useState(0); // 0 for Buyer, 1 for Seller

  useEffect(() => {
    if (heroInView) {
      gsap.fromTo(
        heroElements.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.2 }
      );
    }
  }, [heroInView]);

  useEffect(() => {
    if (featuresInView) {
      gsap.fromTo(
        featureElements.current,
        { x: -50, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.8, stagger: 0.3 }
      );
    }
  }, [featuresInView]);

  useEffect(() => {
    if (statsInView) {
      gsap.fromTo(
        sectionRefs.current,
        { y: 100, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, stagger: 0.3 }
      );
    }
  }, [statsInView]);

  useEffect(() => {
    if (howItWorksInView) {
      gsap.fromTo(
        howItWorksElements.current,
        { scale: 0, opacity: 0, rotation: -180 },
        {
          scale: 1,
          opacity: 1,
          rotation: 0,
          duration: 0.8,
          stagger: 0.2,
          ease: "back.out(1.7)",
        }
      );

      // Animate connecting lines
      gsap.fromTo(
        ".connecting-line",
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 0.5,
          stagger: 0.2,
          delay: 0.8,
          ease: "power2.out",
        }
      );
    }
  }, [howItWorksInView]);

  // const handleRoleChange = (event, newValue) => {
  //   setActiveRole(newValue);
  // };

  // GSAP Animation for Hero Section - Removed due to compilation error
  useEffect(() => {
    if (heroInView) {
      // gsap.fromTo(
      //   heroElements.current,
      //   { y: 50, opacity: 0 },
      //   { y: 0, opacity: 1, duration: 0.8, stagger: 0.2 }
      // );
    }
  }, [heroInView]);

  // GSAP Animation for Features Section - Removed due to compilation error
  useEffect(() => {
    if (featuresInView) {
      // gsap.fromTo(
      //   featureElements.current,
      //   { x: -50, opacity: 0 },
      //   { x: 0, opacity: 1, duration: 0.8, stagger: 0.3 }
      // );
    }
  }, [featuresInView]);

  // GSAP Animation for Stats Section (using sectionRefs for individual stat boxes) - Removed due to compilation error
  useEffect(() => {
    if (statsInView) {
      // gsap.fromTo(
      //   sectionRefs.current,
      //   { y: 100, opacity: 0 },
      //   { y: 0, opacity: 1, duration: 1, stagger: 0.3 }
      // );
    }
  }, [statsInView]);

  // GSAP Animation for How It Works Section - Removed due to compilation error and new tabbed structure
  useEffect(() => {
    if (howItWorksInView) {
      // No GSAP animation for how it works section due to tabbed interface and previous compilation errors
    }
  }, [howItWorksInView]);

  // Auto-scrolling logic for "How It Works" section
  useEffect(() => {
    const scrollSpeed = 1; // Pixels per interval
    const scrollInterval = 30; // Milliseconds per interval

    const autoScroll = (ref) => {
      if (ref.current) {
        let currentScroll = ref.current.scrollLeft;
        const maxScroll = ref.current.scrollWidth - ref.current.clientWidth;

        currentScroll += scrollSpeed;

        // If we've scrolled past the first complete set of items (half the total scroll width),
        // instantly jump back to the beginning of the duplicated set.
        // This creates the illusion of infinite scroll.
        if (currentScroll >= maxScroll / 2) {
          ref.current.scrollLeft = 0; // Reset to the beginning
        } else {
          ref.current.scrollLeft = currentScroll;
        }
      }
    };

    const interval = setInterval(
      () => autoScroll(howItWorksScrollRef),
      scrollInterval
    );

    return () => {
      clearInterval(interval);
    };
  }, [activeRole]); // Re-run effect when activeRole changes to reset scroll

  const testimonials = [
    {
      name: "Jay Patel",
      location: "Melbourne",
      role: "Verified Seller",
      quote:
        "ScrapTrade helped me sell 2 tons of copper in a single day. Incredible efficiency!",
      color: "#F4C542", // Primary color accent
    },
    {
      name: "Priya Malhotra",
      location: "Delhi",
      role: "Verified Seller",
      quote:
        "Got the best aluminum price instantly. Transparent and reliable service!",
      color: "#2D3A27", // Secondary color accent
    },
    {
      name: "Ahmed Khan",
      location: "Hyderabad",
      role: "Premium Buyer",
      quote: "Bid process is smooth. Bought in bulk with confidence!",
      color: "#F4C542", // Primary color accent
    },
    {
      name: "Sara Lee",
      location: "Sydney",
      role: "Verified Buyer",
      quote: "I trust ScrapTrade for scrap sourcing now. Amazing UX!",
      color: "#2D3A27", // Secondary color accent
    },
  ];

  const faqs = [
    {
      question: "How secure are transactions?",
      answer:
        "Bank-level encryption and escrow services protect all transactions with verified dealers.",
    },
    {
      question: "What materials can I trade?",
      answer:
        "All major scrap metals including steel, copper, aluminum, brass, and electronic waste.",
    },
    {
      question: "How is pricing determined?",
      answer:
        "Real-time market rates based on material quality, quantity, location, and current commodity prices.",
    },
    {
      question: "Do you provide logistics support?",
      answer:
        "Yes, we partner with reliable logistics providers to offer seamless pickup and delivery across regions.",
    },
    {
      question: "How do I become a verified dealer?",
      answer:
        "Complete our verification process including business registration, insurance, and reference checks.",
    },
  ];

  // 4 steps for Buyer flow
  const buyerHowItWorksSteps = [
    {
      step: "1",
      title: "Register & Search",
      desc: "Create your buyer profile and search for materials.",
      icon: <FaUser />,
    },
    {
      step: "2",
      title: "Place Bids",
      desc: "Submit competitive bids on desired scrap materials.",
      icon: <FaDollarSign />,
    },
    {
      step: "3",
      title: "Confirm & Pay",
      desc: "Securely confirm your purchase and complete payment.",
      icon: <FaCreditCard />,
    },
    {
      step: "4",
      title: "Receive Delivery",
      desc: "Coordinate logistics and receive your materials.",
      icon: <FaTruck />,
    },
  ];

  // 4 steps for Seller flow
  const sellerHowItWorksSteps = [
    {
      step: "1",
      title: "Register & List",
      desc: "Create your seller profile and list available scrap materials.",
      icon: <FaClipboardList />,
    },
    {
      step: "2",
      title: "Receive Bids",
      desc: "Get instant notifications for bids from verified buyers.",
      icon: <FaChartBar />,
    },
    {
      step: "3",
      title: "Accept & Ship",
      desc: "Accept the best offer and prepare for shipment/pickup.",
      icon: <FaCheckCircle />,
    },
    {
      step: "4",
      title: "Get Paid",
      desc: "Receive secure and timely payment for your sold materials.",
      icon: <FaMoneyBillWave />,
    },
  ];

  // const currentSteps =
  //   activeRole === 0 ? buyerHowItWorksSteps : sellerHowItWorksSteps;

  // Auto-scrolling logic for the Testimonial section
  useEffect(() => {
    const scrollSpeed = 0.5; // Slower scroll for testimonials
    const scrollInterval = 30; // Milliseconds per interval

    const startScrolling = () => {
      scrollIntervalRef.current = setInterval(() => {
        if (testimonialsScrollRef.current) {
          let currentScroll = testimonialsScrollRef.current.scrollLeft;
          const maxScroll =
            testimonialsScrollRef.current.scrollWidth -
            testimonialsScrollRef.current.clientWidth;

          currentScroll += scrollSpeed;

          // Reset to the beginning of the duplicated set
          if (currentScroll >= maxScroll / 2) {
            testimonialsScrollRef.current.scrollLeft = 0;
          } else {
            testimonialsScrollRef.current.scrollLeft = currentScroll;
          }
        }
      }, scrollInterval);
    };

    const stopScrolling = () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };

    startScrolling(); // Start scrolling on mount

    const currentRef = testimonialsScrollRef.current;
    if (currentRef) {
      currentRef.addEventListener("mouseenter", stopScrolling);
      currentRef.addEventListener("mouseleave", startScrolling);
      currentRef.addEventListener("focusin", stopScrolling); // Pause on keyboard focus
      currentRef.addEventListener("focusout", startScrolling); // Resume when focus leaves
    }

    return () => {
      stopScrolling(); // Clear interval on unmount
      if (currentRef) {
        currentRef.removeEventListener("mouseenter", stopScrolling);
        currentRef.removeEventListener("mouseleave", startScrolling);
        currentRef.removeEventListener("focusin", stopScrolling);
        currentRef.removeEventListener("focusout", startScrolling);
      }
    };
  }, []);
  // For GSAP if re-enabled

  return (
    <ThemeProvider theme={theme}>
      <Seo
        title="ScrapTrade Marketplace | Smarter Scrap Trading"
        description="Discover a digital scrap marketplace built for speed, trust, and transparency. Buy or sell scrap online easily with ScrapTrade’s secure platform."
        url="https://scraptrade.com/home"
        canonical="https://scraptrade.com/home"
        image="https://scraptrade.com.au/assets/og/scrap.jpg"
      />

      <Box>
        {/* Hero Section: Displays the main heading, sub-heading, and call-to-action buttons. */}
        <Box ref={heroRef}>
          <HeroSection
            inView={heroInView}
            elementsRef={heroElements}
            themeColors={themeColors}
          />
        </Box>

        {/* How It Works Section: Explains the process for buyers and sellers with auto-scrolling steps. */}
        <Box ref={howItWorksRef}>
          <HowItWorksSection
            inView={howItWorksInView}
            elementsRef={howItWorksElements}
            themeColors={themeColors}
            buyerSteps={buyerHowItWorksSteps}
            sellerSteps={sellerHowItWorksSteps}
          />
        </Box>

        {/* Market Statistics Section: Showcases key numbers and achievements of the platform. */}
        <Box ref={statsRef}>
          <StaticsHomeSection
            inView={statsInView}
            elementsRef={statsRef}
            themeColors={themeColors}
          />
        </Box>

        {/* Why Choose ScrapTrade Section: Highlights the unique benefits and features. */}
        <Box ref={featuresRef}>
          <WhyChooseScrapTradeSection
            inView={featuresInView}
            elementsRef={featureElements}
            themeColors={themeColors}
          />
        </Box>

        <RandomProductHomeSection />

        <TrustAndCredibility />

        {/* Testimonials Section: Displays user testimonials with an auto-scrolling carousel. */}
        <TestimonialsSection
          themeColors={themeColors}
          testimonialsData={testimonials}
        />

        {/* CTA Section */}
        <CTAHomeSection />

        {/* FAQ Section: Provides answers to frequently asked questions in an accordion format. */}
        <FAQSection themeColors={themeColors} faqsData={faqs} />
      </Box>
    </ThemeProvider>
  );
};

export default Landing;
